import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Input,
  message,
  Typography,
  Image,
  Drawer,
  Form,
  Empty,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { getUploadURL } from '../../utils/fileUtils';
import { formatMoney } from '../../utils/formatMoney';
import { formatDateTime, fromNow } from '../../utils/dateFormat';
import { showApiError } from '../../utils/apiError';
import {
  PageHeader,
  StatCard,
  StatGrid,
  TableCard,
  Section,
  statusRenderer,
  REVIEW_STATUS,
  DRAWER_WIDTH,
  MODAL_WIDTH,
  tableDefaults,
  paginationDefaults,
} from '../../components/admin';

const { Text } = Typography;
const { TextArea } = Input;

const renderStatus = statusRenderer(REVIEW_STATUS);

const STATUS_TABS = [
  { key: 'pending', label: 'Pending review', tone: 'warning', icon: <ClockCircleOutlined /> },
  { key: 'approved', label: 'Approved', tone: 'success', icon: <CheckCircleOutlined /> },
  { key: 'rejected', label: 'Rejected', tone: 'danger', icon: <CloseCircleOutlined /> },
  { key: 'all', label: 'All requests', tone: 'default', icon: <InboxOutlined /> },
];

const paymentMethodLabel = (method) =>
  method
    ? String(method)
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())
    : '-';

const TopUpRequestManagement = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [decision, setDecision] = useState(null); // 'approve' | 'reject' | null
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [form] = Form.useForm();

  // Fetch top-up request list. Returns the rows so a caller can move on to
  // the next request in the queue.
  const fetchRequests = useCallback(async (page = 1, pageSize = 10, status = 'pending') => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/topup-requests', {
        params: {
          page,
          limit: pageSize,
          // "All" sends no status; the API treats a missing status as every status.
          status: status === 'all' ? undefined : status,
        },
      });

      if (response.data && response.data.requests) {
        setRequests(response.data.requests);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.pagination?.total ?? response.data.pagination?.totalItems ?? 0,
        });
        const stats = response.data.statistics || {};
        // `statistics.total` counts only the current status filter, so the
        // "All" tile adds the per-status counts instead.
        setStatistics({
          total: (stats.pending ?? 0) + (stats.approved ?? 0) + (stats.rejected ?? 0),
          pending: stats.pending ?? 0,
          approved: stats.approved ?? 0,
          rejected: stats.rejected ?? 0,
        });
        return response.data.requests;
      }
      console.error('Invalid response format:', response.data);
      message.error('Invalid response format from server');
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      showApiError(error, 'Failed to load top-up requests');
    } finally {
      setLoading(false);
    }
    return [];
  }, []);

  useEffect(() => {
    fetchRequests(1, pagination.pageSize, statusFilter);
  }, [fetchRequests, pagination.pageSize, statusFilter]);

  const openRequest = (record) => {
    setSelectedRequest(record);
  };

  const closeDrawer = () => {
    setSelectedRequest(null);
    setDecision(null);
    form.resetFields();
  };

  const openDecision = (action) => {
    form.resetFields();
    setDecision(action);
  };

  // Approve or reject, then open the next pending request so a reviewer can
  // work through the queue without going back to the list each time.
  const handleApproval = async () => {
    if (submittingApproval || !selectedRequest || !decision) return;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const action = decision;
    const verb = action === 'approve' ? 'approved' : 'rejected';
    const verbing = action === 'approve' ? 'approving' : 'rejecting';
    const reviewed = selectedRequest;
    const indexInList = requests.findIndex((r) => r._id === reviewed._id);
    try {
      setSubmittingApproval(true);
      await axios.post(`/admin/topup-requests/${reviewed._id}/${action}`, {
        reviewNotes: values.reason || '',
      });
      setDecision(null);
      form.resetFields();
      const rows = await fetchRequests(pagination.current, pagination.pageSize, statusFilter);
      const nextPending =
        statusFilter === 'pending'
          ? rows.filter((r) => r.status === 'pending' && r._id !== reviewed._id)
          : [];
      if (nextPending.length > 0) {
        const next = nextPending[Math.min(Math.max(indexInList, 0), nextPending.length - 1)];
        setSelectedRequest(next);
        message.success(`${reviewed.requestNumber || 'Request'} ${verb}. Showing the next pending request.`);
      } else {
        setSelectedRequest(null);
        message.success(
          statusFilter === 'pending'
            ? `${reviewed.requestNumber || 'Request'} ${verb}. No more pending requests on this page.`
            : `Top-up request ${verb}`
        );
      }
    } catch (error) {
      console.error(`Error ${verbing} top-up request:`, error);
      showApiError(error, `Failed to ${action} top-up request`);
    } finally {
      setSubmittingApproval(false);
    }
  };

  const columns = [
    {
      title: 'Request',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      width: 200,
      render: (text, record) => (
        <div>
          <Text className="cell-mono" copyable={text ? { text } : false}>
            {text || record._id}
          </Text>
          <div className="cell-sub">{fromNow(record.createdAt)}</div>
        </div>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'clientId',
      key: 'client',
      render: (client) => (
        <div>
          <div className="cell-main">{client?.companyName || client?.name || '-'}</div>
          <div className="cell-sub">{client?.email}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      width: 160,
      render: (_, record) => <span className="cell-money">{formatMoney(record.amount, record.currency)}</span>,
    },
    {
      title: 'Payment method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 160,
      render: (method, record) => (
        <div>
          <div>{paymentMethodLabel(method)}</div>
          {record.paymentReference ? (
            <div className="cell-sub" title={record.paymentReference}>
              Ref {record.paymentReference}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Proof',
      key: 'proof',
      width: 80,
      align: 'center',
      render: (_, record) =>
        record.paymentProof?.filename ? (
          <Tooltip title={record.paymentProof.originalName || 'Payment proof attached'}>
            <FileTextOutlined style={{ color: 'var(--c-primary)' }} />
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: renderStatus,
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: formatDateTime,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      fixed: 'right',
      align: 'right',
      render: (_, record) =>
        record.status === 'pending' ? (
          <Button
            type="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openRequest(record);
            }}
          >
            Review
          </Button>
        ) : (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openRequest(record);
            }}
          >
            View
          </Button>
        ),
    },
  ];

  const activeTab = STATUS_TABS.find((t) => t.key === statusFilter) || STATUS_TABS[0];
  const proof = selectedRequest?.paymentProof;
  const proofUrl = proof?.filename ? getUploadURL('topup-proofs', proof.filename) : null;
  const clientName =
    selectedRequest?.clientId?.companyName || selectedRequest?.clientId?.name || 'this client';

  return (
    <div>
      <PageHeader
        title="Top-up requests"
        subtitle="Check the payment proof, then approve to credit the client's wallet or reject with a reason."
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchRequests(pagination.current, pagination.pageSize, statusFilter)}
            loading={loading}
          >
            Refresh
          </Button>
        }
      />

      <StatGrid>
        {STATUS_TABS.map((tab) => (
          <StatCard
            key={tab.key}
            label={tab.label}
            value={tab.key === 'all' ? statistics.total : statistics[tab.key]}
            icon={tab.icon}
            tone={tab.tone}
            highlight={tab.key === 'pending' && statistics.pending > 0}
            active={statusFilter === tab.key}
            onClick={() => setStatusFilter(tab.key)}
          />
        ))}
      </StatGrid>

      <TableCard title={activeTab.label} count={pagination.total}>
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={requests}
          rowKey="_id"
          loading={loading}
          rowClassName={() => 'row-clickable'}
          onRow={(record) => ({ onClick: () => openRequest(record) })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  statusFilter === 'pending' ? 'No top-ups waiting for review' : 'No top-up requests'
                }
              />
            ),
          }}
          pagination={{
            ...paginationDefaults,
            ...pagination,
            onChange: (page, pageSize) => fetchRequests(page, pageSize, statusFilter),
          }}
        />
      </TableCard>

      <Drawer
        title={
          selectedRequest ? (
            <Space size={10}>
              <span>Top-up {selectedRequest.requestNumber || ''}</span>
              {renderStatus(selectedRequest.status)}
            </Space>
          ) : (
            'Top-up request'
          )
        }
        open={!!selectedRequest}
        onClose={closeDrawer}
        width={DRAWER_WIDTH.lg}
        destroyOnHidden
        footer={
          selectedRequest?.status === 'pending' ? (
            <div className="sheet-footer">
              <Text type="secondary">Approving credits the wallet immediately.</Text>
              <div className="sheet-footer__actions">
                <Button danger icon={<CloseOutlined />} onClick={() => openDecision('reject')}>
                  Reject
                </Button>
                <Button type="primary" icon={<CheckOutlined />} onClick={() => openDecision('approve')}>
                  Approve
                </Button>
              </div>
            </div>
          ) : null
        }
      >
        {selectedRequest && (
          <>
            <Section>
              <div className="summary-head">
                <div>
                  <div className="summary-head__label">Amount</div>
                  <div className="summary-head__amount">
                    {formatMoney(selectedRequest.amount, selectedRequest.currency)}
                  </div>
                </div>
                <div className="summary-head__side">
                  <div className="summary-head__label">Client</div>
                  <div className="cell-main">{clientName}</div>
                  <div className="cell-sub">{selectedRequest.clientId?.email}</div>
                </div>
              </div>
            </Section>

            <Section title="Request details">
              <dl className="kv">
                <dt>Request number</dt>
                <dd>
                  <Text copyable className="cell-mono">
                    {selectedRequest.requestNumber || selectedRequest._id}
                  </Text>
                </dd>
                <dt>Payment method</dt>
                <dd>{paymentMethodLabel(selectedRequest.paymentMethod)}</dd>
                <dt>Payment reference</dt>
                <dd>
                  {selectedRequest.paymentReference ? (
                    <Text copyable>{selectedRequest.paymentReference}</Text>
                  ) : (
                    '-'
                  )}
                </dd>
                <dt>Submitted</dt>
                <dd>
                  {formatDateTime(selectedRequest.createdAt)}{' '}
                  <span className="muted">({fromNow(selectedRequest.createdAt)})</span>
                </dd>
                {selectedRequest.requestedBy ? (
                  <>
                    <dt>Submitted by</dt>
                    <dd>
                      {[selectedRequest.requestedBy.firstName, selectedRequest.requestedBy.lastName]
                        .filter(Boolean)
                        .join(' ') || selectedRequest.requestedBy.email}
                    </dd>
                  </>
                ) : null}
                {selectedRequest.notes || selectedRequest.clientNotes ? (
                  <>
                    <dt>Client note</dt>
                    <dd>{selectedRequest.notes || selectedRequest.clientNotes}</dd>
                  </>
                ) : null}
              </dl>
            </Section>

            <Section
              title="Payment proof"
              description={
                proof?.filename
                  ? [
                      proof.originalName,
                      proof.size ? `${(proof.size / 1024).toFixed(1)} KB` : null,
                      proof.uploadedAt ? `uploaded ${formatDateTime(proof.uploadedAt)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined
              }
              extra={
                proofUrl ? (
                  <a
                    href={proofUrl}
                    download={proof.originalName || proof.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="small" icon={<DownloadOutlined />}>
                      Download
                    </Button>
                  </a>
                ) : null
              }
            >
              {!proofUrl ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payment proof uploaded" />
              ) : proof.mimeType?.startsWith('image/') ? (
                <div className="proof-frame">
                  <Image
                    src={proofUrl}
                    alt="Payment proof"
                    style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }}
                  />
                  <div className="cell-sub" style={{ marginTop: 6 }}>
                    Click the image to enlarge
                  </div>
                </div>
              ) : proof.mimeType === 'application/pdf' ? (
                <div className="proof-frame">
                  <iframe src={proofUrl} title="Payment proof PDF" />
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`No preview for ${proof.mimeType || 'this file type'}. Download it to check.`}
                />
              )}
            </Section>

            {selectedRequest.status !== 'pending' ? (
              <Section title="Review">
                <dl className="kv">
                  <dt>Decision</dt>
                  <dd>{renderStatus(selectedRequest.status)}</dd>
                  <dt>Reviewed</dt>
                  <dd>{formatDateTime(selectedRequest.reviewedAt)}</dd>
                  {selectedRequest.reviewedBy ? (
                    <>
                      <dt>Reviewed by</dt>
                      <dd>
                        {[selectedRequest.reviewedBy.firstName, selectedRequest.reviewedBy.lastName]
                          .filter(Boolean)
                          .join(' ') || selectedRequest.reviewedBy.email}
                      </dd>
                    </>
                  ) : null}
                  {selectedRequest.reviewNotes ? (
                    <>
                      <dt>Notes</dt>
                      <dd>{selectedRequest.reviewNotes}</dd>
                    </>
                  ) : null}
                </dl>
              </Section>
            ) : null}
          </>
        )}
      </Drawer>

      <Modal
        title={decision === 'reject' ? 'Reject top-up request' : 'Approve top-up request'}
        open={!!decision && !!selectedRequest}
        onCancel={() => setDecision(null)}
        width={MODAL_WIDTH.sm}
        destroyOnHidden
        maskClosable={!submittingApproval}
        footer={[
          <Button key="cancel" onClick={() => setDecision(null)} disabled={submittingApproval}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={decision === 'reject'}
            loading={submittingApproval}
            onClick={handleApproval}
          >
            {decision === 'reject' ? 'Reject request' : 'Approve and credit wallet'}
          </Button>,
        ]}
      >
        {selectedRequest ? (
          <>
            <p style={{ marginTop: 0 }}>
              {decision === 'reject' ? (
                <>
                  The client will see this request as rejected, with your reason. Their wallet is not
                  changed.
                </>
              ) : (
                <>
                  <strong>{formatMoney(selectedRequest.amount, selectedRequest.currency)}</strong> will be
                  credited to <strong>{clientName}</strong>'s wallet.
                </>
              )}
            </p>
            <Form form={form} layout="vertical" requiredMark={false}>
              <Form.Item
                name="reason"
                label={decision === 'reject' ? 'Reason for rejection' : 'Note (optional)'}
                rules={
                  decision === 'reject'
                    ? [{ required: true, whitespace: true, message: 'Tell the client why it was rejected' }]
                    : [{ max: 500, message: 'Keep the note under 500 characters' }]
                }
                style={{ marginBottom: 0 }}
              >
                <TextArea
                  rows={3}
                  autoFocus
                  placeholder={
                    decision === 'reject'
                      ? 'e.g. Amount on the bank slip does not match the request'
                      : 'Visible in the review history'
                  }
                />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default TopUpRequestManagement;
